import React, { useEffect, useRef, useState } from 'react';
import { Download } from '@mui/icons-material';
import { Tooltip } from '@mui/material';
import { ArtifactType } from '@/common/const';
import { useHttpContext } from '@/common/hooks/HttpContext';
import { Button } from '@/components/common/button/button';
import { Result, resultIsError } from '@/services/HttpService';

interface ScanSessionCascadingLogActionProps {
    sessionUUID: string;
    artifacts: ArtifactType[];
}

interface LogDownload {
    artifactType: ArtifactType;
    download: string;
    jsonString: string;
}

export const ScanSessionCascadingLogAction = (props: ScanSessionCascadingLogActionProps) => {
    const { sessionUUID, artifacts } = props;

    const hasArCapturePackage = artifacts.includes(ArtifactType.ArCapturePackage);
    const hasBodyHAndArCapturePackage = artifacts.includes(ArtifactType.BodyHAndArCapturePackage);
    const logArtifactTypes: ArtifactType[] = [];
    if (!hasArCapturePackage && !hasBodyHAndArCapturePackage) {
        if (artifacts.includes(ArtifactType.HandsLog)) logArtifactTypes.push(ArtifactType.HandsLog);
        if (artifacts.includes(ArtifactType.PoseLog)) logArtifactTypes.push(ArtifactType.PoseLog);
    }

    const { httpService } = useHttpContext();
    const aRefs = useRef<(HTMLAnchorElement | null)[]>([]);
    const [logDownloads, setLogDownloads] = useState<LogDownload[]>([]);

    const downloadName = (artifactType: ArtifactType) =>
        artifactType === ArtifactType.HandsLog
            ? `Scan_session_hands_log_${sessionUUID}.json`
            : `Scan_session_pose_log_${sessionUUID}.json`;

    const handleClick = async () => {
        if (hasArCapturePackage || hasBodyHAndArCapturePackage) {
            const res = await httpService.adminGetScanSessionArtifactSignedUrl(
                sessionUUID,
                hasArCapturePackage ? ArtifactType.ArCapturePackage : ArtifactType.BodyHAndArCapturePackage,
            );
            if (resultIsError(res)) {
                alert(res.message);
                return;
            }
            window.location.href = res;
            return;
        }

        const stringPrefix = 'text/json;charset=utf-8,';
        const downloads: LogDownload[] = [];
        for (const artifactType of logArtifactTypes) {
            const res = await httpService.adminGetScanSessionArtifactSignedUrl(sessionUUID, artifactType);
            if (resultIsError(res)) {
                alert(res.message);
                continue;
            }
            const logRes: Result<unknown> = await httpService.getExternalUrl(res);
            if (resultIsError(logRes)) {
                console.log('Could not get log.');
                continue;
            }
            downloads.push({
                artifactType,
                download: downloadName(artifactType),
                jsonString: stringPrefix + encodeURIComponent(JSON.stringify(logRes)),
            });
        }
        setLogDownloads(downloads);
    };

    useEffect(() => {
        if (!logDownloads.length) return;
        logDownloads.forEach((_, index) => aRefs.current[index]?.click());
        setLogDownloads([]);
    }, [logDownloads]);

    if (!hasArCapturePackage && !hasBodyHAndArCapturePackage && !logArtifactTypes.length) {
        return (
            <Tooltip title="No log file available">
                <span>
                    <Button onClick={handleClick} startIcon={<Download />} variant="secondary" size="small" disabled>
                        Log
                    </Button>
                </span>
            </Tooltip>
        );
    }

    return (
        <>
            <Button onClick={handleClick} startIcon={<Download />} variant="secondary" size="small">
                Log
            </Button>
            {logDownloads.map((logDownload, index) => (
                <a
                    key={logDownload.artifactType}
                    ref={(el) => {
                        aRefs.current[index] = el;
                    }}
                    style={{ display: 'none' }}
                    href={`data:${logDownload.jsonString}`}
                    download={logDownload.download}
                ></a>
            ))}
        </>
    );
};
